import { BaseApiService } from './base';
import {
  Partner,
  EnhancedPartner,
  PartnerActivitySummary,
  PartnerActivityData,
  CreatePartnerRequest,
  UpdatePartnerRequest,
  PartnerFilters,
  PartnerSortField
} from '../../types/partner';
import { Connection } from '../../types/connection';
import { Task } from '../../types/task';
import { PaginatedResponse, QueryParams } from '../../types/api';
import { snakeToCamel, camelToSnake } from '../../utils/caseTransform';
import {
  CascadeDeleteDependency,
  CascadeDeleteResult,
  DependencyError
} from './cascadeDelete';

// Re-export types for convenience
export type Dependency = CascadeDeleteDependency;
export type DeleteResult = CascadeDeleteResult;
export { DependencyError };

// Transform API response to camelCase Partner type using generic converter
function transformPartner(apiPartner: any): Partner {
  const p = snakeToCamel<any>(apiPartner);

  // T053: Derive health from status if not explicitly provided
  const status = p.status || p.connectionStatus || 'unknown';
  const provisioningStatus = p.provisioningStatus || deriveProvisioningStatus(status);
  const health = p.health || deriveHealthFromStatus(status);

  return {
    partnerId: p.partnerId || '',
    name: p.name || '',
    description: p.description || '',
    isActive: p.isActive ?? true,
    status,
    provisioningStatus,
    health,
    lastActivity: p.lastActivity || '',
    bucketName: p.bucketName || '',
    kmsKeyArn: p.kmsKeyArn || '',
    snsTopicArn: p.snsTopicArn || '',
    taskCounts: p.taskCounts || { total: 0, enabled: 0, disabled: 0, running: 0 },
    createdAt: p.createdAt || '',
    updatedAt: p.updatedAt || '',
    createdBy: p.createdBy || 'system',
    version: p.version
  };
}

// T053: Helper to derive provisioning status from legacy status
function deriveProvisioningStatus(status: string): 'pending' | 'provisioning' | 'active' | 'failed' | 'deleting' {
  switch (status) {
    case 'setting_up':
      return 'provisioning';
    case 'ready':
    case 'idle':
    case 'not_used':
      return 'active';
    case 'issues':
      return 'active'; // Still active, just has health issues
    case 'unknown':
    default:
      return 'pending';
  }
}

// T053: Helper to derive health from legacy status
function deriveHealthFromStatus(status: string): 'healthy' | 'warning' | 'error' | 'unknown' {
  switch (status) {
    case 'ready':
      return 'healthy';
    case 'idle':
    case 'not_used':
      return 'warning';
    case 'issues':
      return 'error';
    case 'setting_up':
    case 'unknown':
    default:
      return 'unknown';
  }
}

class PartnersApiService extends BaseApiService {
  // Partner CRUD operations
  async getPartners(filters?: PartnerFilters, sortBy?: PartnerSortField, sortOrder?: 'asc' | 'desc'): Promise<Partner[]> {
    const params: QueryParams = {};
    
    if (filters) {
      params.filters = [];
      
      if (filters.status.length > 0) {
        params.filters.push({
          field: 'status',
          operator: 'in',
          value: filters.status
        });
      }
      
      if (filters.isActive.length > 0) {
        params.filters.push({
          field: 'isActive',
          operator: 'in',
          value: filters.isActive
        });
      }
      
      if (filters.hasActiveConnections.length > 0) {
        params.filters.push({
          field: 'hasActiveConnections',
          operator: 'in',
          value: filters.hasActiveConnections
        });
      }
      
      if (filters.hasActiveTasks.length > 0) {
        params.filters.push({
          field: 'hasActiveTasks',
          operator: 'in',
          value: filters.hasActiveTasks
        });
      }
    }
    
    if (sortBy && sortOrder) {
      params.sort = [{
        field: sortBy,
        order: sortOrder
      }];
    }

    const response = await this.get('/partners', params);
    const data = await response.json();
    return (data.partners || []).map(transformPartner);
  }

  async getEnhancedPartners(filters?: PartnerFilters): Promise<EnhancedPartner[]> {
    const params: QueryParams = {};
    
    if (filters) {
      params.filters = [];
      
      if (filters.status.length > 0) {
        params.filters.push({
          field: 'status',
          operator: 'in',
          value: filters.status
        });
      }
      
      if (filters.isActive.length > 0) {
        params.filters.push({
          field: 'isActive',
          operator: 'in',
          value: filters.isActive
        });
      }
      
      if (filters.hasActiveConnections.length > 0) {
        params.filters.push({
          field: 'hasActiveConnections',
          operator: 'in',
          value: filters.hasActiveConnections
        });
      }
      
      if (filters.hasActiveTasks.length > 0) {
        params.filters.push({
          field: 'hasActiveTasks',
          operator: 'in',
          value: filters.hasActiveTasks
        });
      }
    }

    const response = await this.get('/partners/enhanced', params);
    const data = await response.json(); return data.map(transformPartner) as EnhancedPartner[];
  }

  async getPartnersPaginated(
    page: number = 1,
    limit: number = 20,
    filters?: PartnerFilters,
    sortBy?: PartnerSortField,
    sortOrder?: 'asc' | 'desc'
  ): Promise<PaginatedResponse<EnhancedPartner>> {
    const params: QueryParams = {
      pagination: { page, limit, sortBy, sortOrder }
    };
    
    if (filters) {
      params.filters = [];
      
      if (filters.status.length > 0) {
        params.filters.push({
          field: 'status',
          operator: 'in',
          value: filters.status
        });
      }
      
      if (filters.isActive.length > 0) {
        params.filters.push({
          field: 'isActive',
          operator: 'in',
          value: filters.isActive
        });
      }
      
      if (filters.hasActiveConnections.length > 0) {
        params.filters.push({
          field: 'hasActiveConnections',
          operator: 'in',
          value: filters.hasActiveConnections
        });
      }
      
      if (filters.hasActiveTasks.length > 0) {
        params.filters.push({
          field: 'hasActiveTasks',
          operator: 'in',
          value: filters.hasActiveTasks
        });
      }
    }

    return this.getPaginated<PaginatedResponse<EnhancedPartner>>('/partners', params);
  }

  async getPartner(id: string): Promise<Partner> {
    const response = await this.get(`/partners/${id}`);
    const data = await response.json();
    return transformPartner(data);
  }

  async getEnhancedPartner(id: string): Promise<EnhancedPartner> {
    const response = await this.get(`/partners/${id}/enhanced`);
    const data = await response.json();
    return transformPartner(data) as EnhancedPartner;
  }

  // THUB-223: `this.post`/`this.put` resolve to a fetch `Response`, not parsed JSON, so
  // these four passed the Response object itself to transformPartner and every field read
  // back `undefined`. Masked because callers refetch the list and render from that; only
  // code using the RETURN VALUE saw the blank object. Same defect class as tasks.ts.
  async createPartner(partnerData: CreatePartnerRequest): Promise<Partner> {
    const response = await this.post('/partners', partnerData);
    const data = await response.json();
    return transformPartner(data);
  }

  async updatePartner(id: string, partnerData: UpdatePartnerRequest): Promise<Partner> {
    const requestData = camelToSnake(partnerData);
    const response = await this.put(`/partners/${id}`, requestData);
    const data = await response.json();
    return transformPartner(data);
  }

  /**
   * Deletes a partner. If cascade is false and the partner has dependencies,
   * throws a DependencyError with the list of dependencies.
   * @param id - The partner ID
   * @param options - Delete options
   * @param options.cascade - If true, deletes all dependent connections and tasks before deleting the partner
   * @returns Delete result with optional cascade deletion summary
   * @throws DependencyError if partner has dependencies and cascade is false
   */
  async deletePartner(id: string, options?: { cascade?: boolean; version?: number }): Promise<DeleteResult> {
    const params = new URLSearchParams();
    if (options?.cascade) params.set('cascade', 'true');
    if (options?.version !== undefined) params.set('version', String(options.version));
    const qs = params.toString();
    const url = `/partners/${id}${qs ? `?${qs}` : ''}`;

    try {
      const response = await this.delete(url);
      const data = await response.json();
      return {
        success: true,
        message: data.message || 'Partner deleted successfully',
        cascadeDeleted: data.cascadeDeleted
      };
    } catch (err: any) {
      // Check for dependency errors in the ApiError details (HTTP 409)
      if (err.statusCode === 409 || err.code === 'CONFLICT') {
        const details = err.details || {};
        // Backend returns 'CANNOT_DELETE_WITH_CHILDREN' with children counts
        if (details.error === 'CANNOT_DELETE_WITH_CHILDREN' || details.error === 'HAS_DEPENDENCIES') {
          // Build dependency list from children counts if individual items not provided
          const dependencies: CascadeDeleteDependency[] = details.dependencies || [];
          const children = details.children || {};
          const connectionCount = children.connections || 0;
          const taskCount = children.tasks || 0;
          const totalCount = details.dependencyCount || (connectionCount + taskCount);

          // If backend only provided counts (not individual items), create placeholder entries
          if (dependencies.length === 0 && totalCount > 0) {
            for (let i = 0; i < connectionCount; i++) {
              dependencies.push({ type: 'connection', id: `connection-${i + 1}`, name: `Connection ${i + 1}` });
            }
            for (let i = 0; i < taskCount; i++) {
              dependencies.push({ type: 'task', id: `task-${i + 1}`, name: `Task ${i + 1}` });
            }
          }

          throw new DependencyError(
            details.message || 'Partner has dependent resources that must be deleted first.',
            dependencies,
            totalCount,
            details.hint || 'Use cascade=true to delete all dependent resources.'
          );
        }
      }
      // Re-throw other errors
      throw err;
    }
  }

  // Partner status management
  async activatePartner(id: string): Promise<Partner> {
    const response = await this.put(`/partners/${id}/activate`);
    const data = await response.json();
    return transformPartner(data);
  }

  async deactivatePartner(id: string): Promise<Partner> {
    const response = await this.put(`/partners/${id}/deactivate`);
    const data = await response.json();
    return transformPartner(data);
  }

  // Partner connections
  async getPartnerConnections(id: string): Promise<Connection[]> {
    const response = await this.get(`/partners/${id}/connections`);
    // Transform snake_case API response to camelCase Connection objects
    const data = await response.json(); 
    return (data.connections || []).map((raw: any) => {
      const conn = snakeToCamel<any>(raw);
      const connectionType = conn.connectionType || 'inbound';
      return {
        connectionId: conn.connectionId || '',
        name: conn.name || '',
        partnerId: conn.partnerId || '',
        partnerName: conn.partnerName || '',
        hostingType: conn.hostingType || (connectionType === 'inbound' ? 'hosted' : 'remote'),
        transferMethod: conn.transferMethod || 'sftp',
        config: conn.config || {},
        status: conn.status || 'unknown',
        lastConnectionTest: conn.lastConnectionTest || '',
        lastSuccessfulConnection: conn.lastSuccessfulConnection || '',
        connectionTestResults: conn.connectionTestResults || null,
        lastTransferAt: conn.lastTransferAt || '',
        lastSuccessfulTransferAt: conn.lastSuccessfulTransferAt || '',
        lastFailedTransferAt: conn.lastFailedTransferAt || '',
        transferSuccessRate30d: conn.transferSuccessRate30d || 0,
        totalTransfers30d: conn.totalTransfers30d || 0,
        successfulTransfers30d: conn.successfulTransfers30d || 0,
        failedTransfers30d: conn.failedTransfers30d || 0,
        bytesTransferred30d: conn.bytesTransferred30d || 0,
        filesTransferred30d: conn.filesTransferred30d || 0,
        transferFamilyUserArn: conn.transferFamilyUserArn || '',
        transferFamilyConnectorArn: conn.transferFamilyConnectorArn || '',
        transferFamilyConnectorId: conn.transferFamilyConnectorId || '',
        taskCounts: conn.taskCounts || { total: 0, enabled: 0, disabled: 0, running: 0 },
        createdAt: conn.createdAt || '',
        updatedAt: conn.updatedAt || '',
        createdBy: conn.createdBy || 'system'
      };
    });
  }

  // Partner tasks (across all connections)
  async getPartnerTasks(id: string): Promise<Task[]> {
    const response = await this.get(`/partners/${id}/tasks`);
    // Transform snake_case API response to camelCase Task objects
    const data = await response.json(); 
    return (data.tasks || []).map((raw: any) => {
      const task = snakeToCamel<any>(raw);
      return {
        taskId: task.taskId || '',
        name: task.name || '',
        partnerId: task.partnerId || '',
        partnerName: task.partnerName || '',
        connectionId: task.connectionId || '',
        connectionName: task.connectionName || '',
        transferDirection: task.transferDirection ||
          (task.transferType === 'receive' || task.transferType === 'pickup' ? 'inbound' : 'outbound'),
        status: task.status || 'disabled',
        configuration: task.config || task.configuration || {},
        schedule: task.schedule || undefined,
        lastExecution: task.lastExecution || undefined,
        metrics: {
          totalExecutions: task.runCount || 0,
          successfulExecutions: task.successCount || 0,
          failedExecutions: task.failureCount || 0,
          averageExecutionTime: task.avgExecutionTime || 0,
          totalFilesProcessed: task.totalFilesProcessed || 0,
          totalBytesTransferred: task.totalBytesTransferred || 0,
          lastSuccessfulExecution: task.lastSuccessAt || undefined,
          lastFailedExecution: task.lastFailureAt || undefined
        },
        eventBridgeRuleArn: task.eventBridgeRuleArn || undefined,
        createdAt: task.createdAt || '',
        updatedAt: task.updatedAt || '',
        createdBy: task.createdBy || 'system'
      };
    });
  }

  // Partner analytics
  async getPartnerAnalytics(id: string): Promise<{
    connectionHealth: {
      total: number;
      healthy: number;
      warning: number;
      critical: number;
    };
    taskPerformance: {
      total: number;
      enabled: number;
      disabled: number;
      successRate: number;
    };
    activitySummary: PartnerActivitySummary;
  }> {
    const response = await this.get(`/partners/${id}/analytics`);
    return response.json();
  }

  async getPartnerActivitySummary(id: string): Promise<PartnerActivitySummary> {
    const response = await this.get(`/partners/${id}/activity-summary`);
    return response.json();
  }

  async getPartnerActivityData(
    timeRange: '30d' | '90d' = '30d',
    limit: number = 10
  ): Promise<PartnerActivityData[]> {
    const params: QueryParams = {
      filters: [
        { field: 'timeRange', operator: 'eq', value: timeRange },
        { field: 'limit', operator: 'eq', value: limit }
      ]
    };

    const response = await this.get('/partners/activity-data', params);
    return response.json();
  }

  // Partner statistics
  async getPartnerStatistics(): Promise<{
    totalPartners: number;
    activePartners: number;
    inactivePartners: number;
    partnersWithConnections: number;
    partnersWithTasks: number;
  }> {
    const response = await this.get('/partners/statistics');
    return response.json();
  }

  // Partner validation
  async validatePartnerConfiguration(
    partnerData: CreatePartnerRequest | UpdatePartnerRequest
  ): Promise<{ valid: boolean; errors: string[] }> {
    const response = await this.post(
      '/partners/validate-configuration',
      partnerData
    );
    return response.json();
  }

  /**
   * THUB-197: Generate a pre-signed download URL for a file in the partner's bucket.
   *
   * Backend route: GET /partners/{id}/files/{fileKey}/download
   * (download_partner_file_handler in partner_management_api/index.py).
   *
   * URL SHAPE — important: the API Gateway resource is the NON-greedy single
   * segment `files/{fileKey}/download` (api-nested-stack.ts), and the Lambda
   * decodes the key with urllib.parse.unquote(). Archive keys contain slashes
   * (e.g. `archive/inbound/task/2026/01/15/data.csv`), so the key MUST be sent
   * as a SINGLE percent-encoded path segment via encodeURIComponent — raw
   * slashes would split into multiple segments and fail to match the resource.
   *
   * Server-side partner/Okta-group scoping is enforced by the handler; only
   * offer this in the UI for partners/files the user can already see.
   *
   * @param partnerId - The partner ID that owns the file
   * @param fileKey - The bare S3 object key (no leading slash, no bucket prefix)
   * @returns The pre-signed download URL plus filename/expiry metadata
   */
  async downloadPartnerFile(
    partnerId: string,
    fileKey: string
  ): Promise<{ downloadUrl: string; filename: string; fileKey: string; expiresIn: number; expiresAt: string }> {
    // Strip any accidental leading slash so the key stays bare before encoding.
    const bareKey = fileKey.replace(/^\/+/, '');
    const encodedKey = encodeURIComponent(bareKey);
    const response = await this.get(`/partners/${partnerId}/files/${encodedKey}/download`);
    const data = await response.json();
    return {
      downloadUrl: data.downloadUrl,
      filename: data.filename || bareKey.split('/').pop() || 'download',
      fileKey: data.fileKey || bareKey,
      expiresIn: data.expiresIn,
      expiresAt: data.expiresAt
    };
  }

  async checkPartnerNameAvailability(name: string, excludeId?: string): Promise<{ available: boolean }> {
    const params: QueryParams = {
      filters: [
        { field: 'name', operator: 'eq', value: name }
      ]
    };
    
    if (excludeId) {
      params.filters!.push({ field: 'excludeId', operator: 'eq', value: excludeId });
    }

    const response = await this.get('/partners/check-name', params);
    return response.json();
  }
}

// Export singleton instance
export const partnersApi = new PartnersApiService();